import { ArrowUp, Loader2, Square } from "lucide-react";
import { useEffect, useRef, useState, type KeyboardEvent } from "react";

import { Textarea } from "@/components/ui/textarea";
import { useChat } from "@/contexts/ChatContext";
import { useIsMobile } from "@/hooks/use-mobile";

import {
  COMPOSER_PLACEHOLDER_IDLE,
  COMPOSER_PLACEHOLDER_RATE_LIMITED,
  COMPOSER_PLACEHOLDER_STREAMING,
  SEND_LABEL,
  STOP_LABEL,
  formatCountdown,
} from "./chatCopy";

/**
 * The composer: one auto-growing textarea and one 44x44 action slot that holds send OR stop.
 *
 * WHY THE LOCKED TEXTAREA IS `readOnly` AND NOT `disabled`, which is the single most important line in
 * this file: a `disabled` element LEAVES THE TAB ORDER, and an element that becomes disabled while
 * focused blurs to `<body>` -- which drops a screen-reader user to the top of the page mid-answer.
 * `readOnly` plus `aria-disabled` keeps focus and tab order intact, which is also what keeps the stop
 * button exactly one `Tab` away from where focus already is while a run streams.
 *
 * WHY STOP REUSES SEND'S SLOT. Same 44x44 box, same position. That is what makes it reachable with one
 * `Tab` instead of a hunt, and the cost -- an accessible name that mutates in place -- is paid by the
 * message list's announcer, item (c).
 *
 * THE STOP BUTTON IS NEUTRAL, NEVER DESTRUCTIVE-COLOURED. D-08 keeps the partial text, so stopping
 * destroys nothing, and painting it red would lie about the outcome. There is also NO ARE-YOU-SURE
 * STEP OF ANY KIND, ever: stopping is not destructive, and a second dialog burns tokens while the
 * visitor reads it, which defeats the button's whole purpose.
 */

/**
 * The hard input bound, matching the server's own.
 *
 * `chat-agent`'s web adapter declares `maxTextChars` from `ENVELOPE_BOUNDS.maxTextChars` and the send
 * route rejects anything longer with a 400. Bounding the field at the same number means the composer
 * cannot compose a message that is guaranteed to be refused; it is not a display cap and there is no
 * counter, because a character count on a chat box is a thing to watch instead of a thing to say.
 */
const MAX_MESSAGE_CHARS = 8000;

/** ~5 lines of Arabic at 14px/1.6 before the field scrolls internally. */
const MAX_TEXTAREA_HEIGHT_PX = 120;

export default function ChatComposer() {
  const {
    streaming,
    stopPending,
    limit,
    countdownSec,
    composerLocked,
    pageContext,
    sendMessage,
    stopGeneration,
  } = useChat();
  const isMobile = useIsMobile();

  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  // Auto-grow: reset then measure, so the field SHRINKS again after a send or a deletion. Capped, at
  // which point the textarea's own overflow takes over.
  useEffect(() => {
    const element = inputRef.current;
    if (element === null) return;
    element.style.height = "auto";
    element.style.height = `${Math.min(element.scrollHeight, MAX_TEXTAREA_HEIGHT_PX)}px`;
  }, [value]);

  const rateLimited = limit !== null;
  const hasText = value.trim() !== "";

  const placeholder = streaming
    ? COMPOSER_PLACEHOLDER_STREAMING
    : rateLimited
      ? COMPOSER_PLACEHOLDER_RATE_LIMITED
      : COMPOSER_PLACEHOLDER_IDLE;

  const submit = () => {
    if (composerLocked || !hasText) return;
    // THE PAGE CONTEXT IS CAPTURED HERE, AT SEND TIME (D-14), not once per conversation. A visitor who
    // asks "الأسعار هنا كويسة؟", reads the answer and then navigates to another store must have the
    // NEXT message carry the new page -- and the previous message must keep the page it was asked on.
    sendMessage({ text: value, pageContext });
    setValue("");
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter") return;
    // ON A PHONE, `Enter` ALWAYS INSERTS A NEWLINE and only the button sends. Mobile keyboards put
    // Enter exactly where a newline key belongs, and accidental half-sends are the standard complaint
    // about chat boxes that treat it as submit. One hook, one breakpoint: `useIsMobile()` already owns
    // the 768px number and a second media query here would be a second thing to keep in step.
    if (isMobile) return;
    if (event.shiftKey) return;
    event.preventDefault();
    submit();
  };

  return (
    <div className="shrink-0 border-t border-[var(--ys-rule)] bg-[var(--ys-paper)] px-4 py-3">
      {/* D-16's countdown, aligned to the inline-start above the field. Western digits, matching every
          other number on the site: a countdown is the one place a reader is scanning for a figure. */}
      {rateLimited ? (
        <div
          className="mb-2 text-start"
          style={{ fontSize: 13, fontWeight: 400, lineHeight: 1.5, color: "var(--ys-ink-2)" }}
        >
          {formatCountdown(countdownSec)}
        </div>
      ) : null}

      <div className="flex items-end gap-2">
        <Textarea
          ref={inputRef}
          // The seam `ChatPanel`'s focus-on-open effect looks for. Focus lands here rather than on the
          // card, so the visitor can start typing the moment the panel appears.
          data-chat-composer-input="true"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          rows={1}
          maxLength={MAX_MESSAGE_CHARS}
          // `readOnly` + `aria-disabled`, deliberately NOT `disabled`. See the docblock: a disabled
          // field leaves the tab order and blurs a focused visitor to the document body mid-answer.
          readOnly={composerLocked}
          aria-disabled={composerLocked ? "true" : undefined}
          className={`min-h-11 max-h-[120px] flex-1 resize-none overflow-y-auto rounded-[var(--ys-r-md)] border-[var(--ys-rule)] bg-[var(--ys-bg)] px-3 py-3 text-[var(--ys-ink)] placeholder:text-[var(--ys-ink-2)] focus-visible:ring-[var(--ys-ink-2)] focus-visible:ring-offset-2 ${
            composerLocked ? "opacity-60" : ""
          }`}
          style={{ fontSize: 14, fontWeight: 400, lineHeight: 1.6 }}
        />

        {/* THE ONE ACTION SLOT. Streaming swaps its contents from send to stop and its position does
            not move, which is the whole reason stop is one Tab from the composer. */}
        {streaming ? (
          <button
            type="button"
            onClick={() => stopGeneration()}
            aria-label={STOP_LABEL}
            // The accessible name does NOT change while the abort POST is in flight: the button still
            // means "stop the reply", it is simply busy asking. A desktop-only `title` carries the
            // same string on hover; a tooltip primitive would need a provider mounted in the panel and
            // would add no accessible name that this label does not already give.
            title={isMobile ? undefined : STOP_LABEL}
            aria-disabled={stopPending ? "true" : undefined}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--ys-r-md)] border border-[var(--ys-rule-2)] bg-[var(--ys-paper)] text-[var(--ys-ink-2)] transition-colors hover:bg-[var(--ys-bg-2)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ys-ink-2)] focus-visible:ring-offset-2"
          >
            {stopPending ? (
              // The pending state, and there is NO optimistic terminal transition behind it: the turn
              // keeps streaming until the server says otherwise. If the abort fails, a system row says
              // so, this button returns to enabled, and the composer stays locked -- because the bot is
              // in fact still talking. A second press retries.
              <Loader2 size={16} strokeWidth={2} aria-hidden="true" className="animate-spin" />
            ) : (
              <Square size={16} strokeWidth={0} fill="currentColor" aria-hidden="true" />
            )}
          </button>
        ) : (
          <button
            type="button"
            onClick={submit}
            aria-label={SEND_LABEL}
            disabled={composerLocked || !hasText}
            // `--ys-orange-600` and not the bare primary orange: white on the primary measures 2.81:1
            // and fails SC 1.4.11 for the glyph. Darkening the fill one stop costs nothing
            // semantically, because that stop is already this button's hover colour everywhere else on
            // the site, and it takes the glyph to 3.71:1.
            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-[var(--ys-r-md)] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
              composerLocked || !hasText
                ? "bg-[var(--ys-bg-2)] text-[var(--ys-ink-2)] focus-visible:ring-[var(--ys-ink-2)]"
                : "bg-[var(--ys-orange-600)] text-white hover:bg-[var(--ys-orange-700)] focus-visible:ring-[var(--ys-orange-700)]"
            }`}
          >
            {/* `ArrowUp`, never a paper plane. A horizontal plane has to be mirrored under RTL and
                gets it wrong about half the time; a vertical arrow is direction-neutral. */}
            <ArrowUp size={20} strokeWidth={2} aria-hidden="true" />
          </button>
        )}
      </div>
    </div>
  );
}
